import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import WorkflowBuilderComponent from '../components/WorkflowBuilder/WorkflowBuilder';

export default function WorkflowBuilder() {
  const { id: workflowId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [workflow, setWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchWorkflow = useCallback(async () => {
    try {
      const response = await apiRequest(`/workflows/${workflowId}`);
      if (response.ok) {
        const data = await response.json();
        setWorkflow(data);
      } else {
        throw new Error('Failed to fetch workflow');
      }
    } catch (error) {
      console.error('Error fetching workflow:', error);
      showError('Failed to load workflow');
      navigate('/workflows');
    }
  }, [workflowId, navigate, showError]);

  useEffect(() => {
    fetchWorkflow();
  }, [workflowId, fetchWorkflow]);

  const handleSave = async (definition, name) => {
    try {
      setSaving(true);
      const response = await apiRequest(`/workflows/${workflowId}`, {
        method: 'PUT',
        body: JSON.stringify({ definition, name }),
      });

      if (response.ok) {
        const saved = await response.json();
        setWorkflow(saved);
        showSuccess('Workflow saved');
      } else {
        throw new Error('Failed to save workflow');
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      showError('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      const response = await apiRequest(`/workflows/${workflowId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        const saved = await response.json();
        setWorkflow(saved);
        showSuccess(newStatus === 'active' ? 'Workflow activated' : 'Workflow deactivated');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating workflow status:', error);
      showError('Failed to update workflow status');
    } finally {
      setSaving(false);
    }
  };

  if (!workflow) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500 mt-3">Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <WorkflowBuilderComponent
      workflow={workflow}
      onSave={handleSave}
      onStatusChange={handleStatusChange}
      saving={saving}
    />
  );
}
